import { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@/fontawesome';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { calculateSessionProfit, calculatePersonalSessionProfit, SESSION_BUDGET } from '@/utils/daytrade-session-profit';
import type { DayTradingSession } from '@/models/day-trading-session';

interface Props {
  sessions: DayTradingSession[];
}

interface ChartPoint {
  date: string;
  theoretical: number;
  personal: number;
}

interface CustomTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ name: string; value: number; color: string }>;
}

const LOCAL_STORAGE_KEY = 'daytrading_depot_size';
const COLOR_THEORETICAL = '#22c55e';
const COLOR_PERSONAL = '#3b82f6';

function readStoredDepot(): number {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw === null) return SESSION_BUDGET;
    const parsed = Number.parseFloat(raw);
    if (Number.isNaN(parsed) || parsed <= 0) return SESSION_BUDGET;
    return parsed;
  } catch {
    return SESSION_BUDGET;
  }
}

function CustomTooltip({ active, label, payload }: CustomTooltipProps) {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 shadow-md">
      <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs" style={{ color: p.color }}>
          <span className="font-medium">{p.name}:</span> {formatCurrency(p.value, 'USD')}
        </p>
      ))}
    </div>
  );
}

export default function DayTradingResultsGraph({ sessions }: Props) {
  const [depotSize, setDepotSize] = useState<number>(() => readStoredDepot());
  const [depotInput, setDepotInput] = useState<string>(() => readStoredDepot().toString());

  useEffect(() => {
    if (depotSize > 0) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, depotSize.toString());
      } catch {
        // Silently ignore storage errors
      }
    }
  }, [depotSize]);

  const chartData = useMemo<ChartPoint[]>(() => {
    if (sessions.length === 0 || depotSize <= 0) return [];

    const sorted = [...sessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    let cumTheo = 0;
    let cumPers = 0;
    const points: ChartPoint[] = [];

    for (const session of sorted) {
      const trades = session.state.trades ?? [];
      const theo = calculateSessionProfit(trades, depotSize).profit;
      const personal = calculatePersonalSessionProfit(trades, session.participant, depotSize);
      const pers = personal !== null ? personal.profit : 0;

      cumTheo += theo;
      cumPers += pers;

      points.push({
        date: formatDate(session.start),
        theoretical: cumTheo,
        personal: cumPers,
      });
    }

    return points;
  }, [sessions, depotSize]);

  const handleDepotBlur = () => {
    const parsed = Number.parseFloat(depotInput);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setDepotInput(depotSize.toString());
      return;
    }
    setDepotSize(parsed);
    setDepotInput(parsed.toString());
  };

  const finalTheo = chartData.length > 0 ? chartData[chartData.length - 1].theoretical : 0;
  const finalPers = chartData.length > 0 ? chartData[chartData.length - 1].personal : 0;

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dein Depot-Verlauf</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Kumulierter Gewinn/Verlust über alle Sessions, skaliert auf deine Depot-Größe.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="daytrading-depot-size" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Depot-Größe ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                id="daytrading-depot-size"
                type="number"
                min="0"
                step="1000"
                value={depotInput}
                onChange={(e) => setDepotInput(e.target.value)}
                onBlur={handleDepotBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                className="w-full pl-7 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="flex gap-6 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Theoretisch</p>
                <p className="font-semibold" style={{ color: COLOR_THEORETICAL }}>
                  {formatCurrency(finalTheo, 'USD')}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Persönlich</p>
                <p className="font-semibold" style={{ color: COLOR_PERSONAL }}>
                  {formatCurrency(finalPers, 'USD')}
                </p>
              </div>
            </div>
          )}
        </div>

        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400 text-sm">
            {sessions.length === 0
              ? 'Noch keine abgeschlossenen Sessions vorhanden.'
              : 'Bitte eine gültige Depot-Größe eingeben.'}
          </div>
        ) : (
          <div className="w-full" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  className="text-gray-600 dark:text-gray-400"
                  tickMargin={8}
                  minTickGap={30}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  className="text-gray-600 dark:text-gray-400"
                  tickFormatter={(value: number) => formatCurrency(value, 'USD')}
                  tickMargin={8}
                  width={90}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="theoretical"
                  name="Theoretisch"
                  stroke={COLOR_THEORETICAL}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="personal"
                  name="Persönlich"
                  stroke={COLOR_PERSONAL}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
